import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import { navigationItems } from "../utils/utils";

const Navbar = ({ searchAction }) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const searchRef = useRef(null);
  const containerRef = useRef(null);

  const toggleSearch = () => setIsSearchOpen((prev) => !prev);
  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  // Function to check if a nav item is active
  const isActive = (item) => {
    if (item.isHome) {
      return location.pathname === "/";
    }
    if (item.category) {
      // Handle URL encoding for Albanian characters
      const decodedPathname = decodeURIComponent(location.pathname);
      const isActivePath =
        decodedPathname === item.path ||
        decodedPathname.includes(`/category/${item.category}`);
      return isActivePath;
    }
    return location.pathname === item.path;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && searchTerm.trim()) {
      navigate(`/search?s=${encodeURIComponent(searchTerm.trim())}`);
      setIsSearchOpen(false);
      setSearchTerm("");
    }
    if (e.key === "Escape") {
      setIsSearchOpen(false);
    }
  };

  const handleClickOutside = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setIsSearchOpen(false);
    }
  };

  const handleSearchSubmit = () => {
    if (searchTerm.trim()) {
      navigate(`/search?s=${encodeURIComponent(searchTerm.trim())}`);
      setIsSearchOpen(false);
      setSearchTerm("");
    }
  };

  // Prevent body scroll when sidebar is open (only on small screens)
  useEffect(() => {
    if (isSidebarOpen) {
      // Only prevent scrolling on small screens (mobile devices)
      if (window.innerWidth <= 900) {
        document.body.classList.add("sidebar-open");
      }
    } else {
      document.body.classList.remove("sidebar-open");
    }
    return () => {
      document.body.classList.remove("sidebar-open");
    };
  }, [isSidebarOpen]);

  // Handle window resize to update scroll behavior
  useEffect(() => {
    const handleResize = () => {
      if (isSidebarOpen) {
        if (window.innerWidth <= 900) {
          document.body.classList.add("sidebar-open");
        } else {
          document.body.classList.remove("sidebar-open");
        }
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isSidebarOpen]);

  // Focus trap for sidebar (basic)
  useEffect(() => {
    if (isSidebarOpen) {
      const handleTab = (e) => {
        const focusableEls = document.querySelectorAll(
          '.sidebar [tabindex="0"], .sidebar button, .sidebar a, .sidebar input'
        );
        const firstEl = focusableEls[0];
        const lastEl = focusableEls[focusableEls.length - 1];
        if (e.key === "Tab") {
          if (e.shiftKey) {
            if (document.activeElement === firstEl) {
              e.preventDefault();
              lastEl.focus();
            }
          } else {
            if (document.activeElement === lastEl) {
              e.preventDefault();
              firstEl.focus();
            }
          }
        }
        if (e.key === "Escape") {
          setIsSidebarOpen(false);
        }
      };
      document.addEventListener("keydown", handleTab);
      return () => document.removeEventListener("keydown", handleTab);
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    if (isSearchOpen && searchRef.current) {
      searchRef.current.focus();
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSearchOpen]);

  return (
    <header className="modern-header">
      <div className="container">
        <div className="header-wrapper">
          <div className="logo-section">
            <Link to="/" className="logo-link">
              <p
                style={{
                  color: "#e31e24",
                  fontWeight: "800",
                  fontSize: "1.5rem",
                  letterSpacing: "0.05em",
                  margin: 0,
                  fontFamily:
                    "'Bebas Neue', 'Impact', 'Arial Black', sans-serif",
                  textTransform: "uppercase",
                }}
              >
                BALOTAZH.COM
              </p>
            </Link>
          </div>

          {/* Main navigation: hidden on mobile, visible on desktop */}
          <nav className="main-navigation">
            <ul className="nav-list">
              {navigationItems.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    className={isActive(item) ? "nav-link active" : "nav-link"}
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="header-actions" ref={containerRef}>
            {/* Enhanced Search Section */}
            <div className="search-section">
              <button
                className="search-toggle"
                onClick={toggleSearch}
                aria-label="Toggle search input"
                aria-expanded={isSearchOpen}
                aria-haspopup="true"
                style={{ background: "transparent", border: "none" }}
              >
                <i className="fas fa-search"></i>
              </button>

              {isSearchOpen && (
                <div className="enhanced-search-wrapper">
                  <div className="search-input-container">
                    <input
                      type="text"
                      ref={searchRef}
                      className="enhanced-search-input"
                      placeholder="Kërko këtu..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={handleKeyDown}
                      aria-label="Search input"
                    />
                    <button
                      className="search-submit-btn"
                      onClick={handleSearchSubmit}
                      aria-label="Submit search"
                    >
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Hamburger menu for mobile */}
            <button
              className="menu-toggle ml-3"
              onClick={toggleSidebar}
              aria-label="Open menu"
            >
              <i className="fas fa-bars"></i>
            </button>

            {/* Sidebar overlay and sidebar */}
            {isSidebarOpen && (
              <>
                <div
                  className="sidebar-overlay"
                  onClick={() => setIsSidebarOpen(false)}
                  aria-label="Close menu overlay"
                  tabIndex={0}
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    background: "rgba(0,0,0,0.4)",
                    zIndex: 1000,
                  }}
                />
                <Sidebar
                  isOpen={isSidebarOpen}
                  onClose={() => setIsSidebarOpen(false)}
                  aria-modal="true"
                  className="sidebar"
                />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
